import type { APIMessageReference } from '../contracts/chat-types.js';
import type { KeyValueStorage } from './storage.js';

const KEY = 'chat:fila-de-envio:';

/** Mensagem de texto esperando conexão (arquivo não entra: some ao fechar o app). */
export type QueuedMessage = {
  nonce: string;
  channelId: string;
  content: string;
  createdAt: number;
  messageReference?: APIMessageReference | null;
};

/**
 * Fila de envio (design C24/M30): o que não saiu por falta de conexão fica
 * aqui, em ordem, e sai quando o gateway volta. Gravada no armazenamento
 * local para não perder ao fechar o app.
 */
export class Outbox {
  private items: QueuedMessage[] = [];
  private loaded = false;

  constructor(
    private readonly storage: KeyValueStorage,
    private readonly scope: string = 'padrao',
  ) {}

  async load(): Promise<QueuedMessage[]> {
    if (!this.loaded) {
      const raw = await this.storage.getItem(KEY + this.scope);
      try {
        this.items = raw ? (JSON.parse(raw) as QueuedMessage[]) : [];
      } catch {
        this.items = [];
      }
      this.loaded = true;
    }
    return [...this.items];
  }

  list(): QueuedMessage[] {
    return [...this.items];
  }

  async add(message: QueuedMessage): Promise<void> {
    await this.load();
    this.items = [
      ...this.items.filter((item) => item.nonce !== message.nonce),
      message,
    ];
    await this.save();
  }

  async remove(nonce: string): Promise<void> {
    await this.load();
    const before = this.items.length;
    this.items = this.items.filter((item) => item.nonce !== nonce);
    if (this.items.length !== before) await this.save();
  }

  async clear(): Promise<void> {
    this.items = [];
    this.loaded = true;
    await this.storage.removeItem(KEY + this.scope);
  }

  private async save(): Promise<void> {
    if (this.items.length) {
      await this.storage.setItem(KEY + this.scope, JSON.stringify(this.items));
    } else {
      await this.storage.removeItem(KEY + this.scope);
    }
  }
}
